import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import Swal from 'sweetalert2';
import aboutHeroImg from './assets/aboutpage.png';
import giphyImg from './assets/giphy.jpg';

const SelfMode = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [currentTime, setCurrentTime] = useState(moment());
    const [patientName, setPatientName] = useState('');
    const [patientMobile, setPatientMobile] = useState('');
    const [doctorsList, setDoctorsList] = useState([]);
    const [selectedDoctor, setSelectedDoctor] = useState(null);
    const token = localStorage.getItem("tokenly_token");
    const userData = JSON.parse(localStorage.getItem("tokenly_user") || "{}");

    // ─── Fetch Doctors List ─────────────────────────────────────
    useEffect(() => {
        const fetchDoctors = async () => {
            try {
                const userData = JSON.parse(localStorage.getItem("tokenly_user") || "{}");
                const res = await fetch("https://tokenly.biswaas.com/api/doctor/doctorDashboardDataV15", {
                    method: "POST",
                    headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
                    body: JSON.stringify({ version: 15 })
                });
                const json = await res.json();
                if (json.status && json.data?.staffDoctorsList) {
                    setDoctorsList(json.data.staffDoctorsList);

                    // Find the logged-in doctor from the list
                    const loggedInDoctor = json.data.staffDoctorsList.find(doctor =>
                        doctor.id === userData.doctorId || doctor.id === userData.userId
                    );

                    if (loggedInDoctor) {
                        setSelectedDoctor(loggedInDoctor);
                    } else if (json.data.staffDoctorsList.length > 0) {
                        // Fallback to first doctor if logged-in doctor not found
                        setSelectedDoctor(json.data.staffDoctorsList[0]);
                    }
                }
            } catch (error) {
                console.error("Error fetching doctors:", error);
            }
        };
        fetchDoctors();
    }, [token]);

    // ─── Live Clock Effect ─────────────────────────────────────────────────────
    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentTime(moment());
        }, 1000);
        return () => clearInterval(timer);
    }, []);

    // ─── Native Logic: Shift Calculation ──────────────────────────────────────
    const getShiftInfo = (doctor) => {
        if (!doctor?.hours_detail?.time) return { index: 0, timeStr: 'N/A', isDisable: false };

        const now = moment();
        let currentShift = doctor.current_token; // From API
        let upcomingShift = null;
        let timeStr = 'N/A';
        let isDisable = false;

        doctor.hours_detail.time.forEach((hd, idx) => {
            const start = moment(hd.start_time, 'hh:mm A').subtract(30, 'minutes');
            const end = moment(hd.end_time, 'hh:mm A').add(30, 'minutes');

            if (now.isAfter(start) && now.isBefore(end) && currentShift === null) {
                currentShift = idx;
            } else if (now.isBefore(start) && currentShift === null && upcomingShift === null) {
                upcomingShift = idx;
            }
        });

        if (currentShift === null) currentShift = upcomingShift;
        if (currentShift !== null && doctor.hours_detail.time[currentShift]) {
            const sh = doctor.hours_detail.time[currentShift];
            timeStr = `${sh.start_time} - ${sh.end_time}`;
            if (now.isAfter(moment(sh.end_time, 'hh:mm A'))) isDisable = true;
        }

        return { index: currentShift, timeStr, isDisable };
    };

    const executeSMSToken = async (name, mobile) => {
        if (!selectedDoctor) {
            Swal.fire('Error', 'No doctor selected. Please refresh the page.', 'error');
            return;
        }

        const { index: shiftIndex, timeStr, isDisable } = getShiftInfo(selectedDoctor);

        if (shiftIndex === null || isDisable) {
            Swal.fire('Unavailable', 'The doctor is currently unavailable or shift has ended. Cannot generate token.', 'warning');
            return;
        }

        setLoading(true);

        // If name and mobile are empty, use default values (Matches StaffDoctorsList logic)
        const tokenName = (!name || name.trim() === '') ? 'Self' : name;
        const tokenMobile = (!mobile || mobile.trim() === '') ? 'Self' : mobile;

        try {
            const res = await fetch("https://tokenly.biswaas.com/api/doctor/genrateToken", {
                method: "POST",
                headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
                body: JSON.stringify({
                    itemId: selectedDoctor.id,
                    shift: shiftIndex ?? 0,
                    action: 'print',
                    name: tokenName,
                    mobile: tokenMobile,
                    isSMS: 1, // Send SMS only
                    printerType: 'blu',
                    shift_time: timeStr
                })
            });
            const json = await res.json();
            if (json.status) {
                if (json.data && json.data.token_no) {
                    const shiftTime = json.data.shift_time || timeStr;
                    Swal.fire({
                        title: 'Token Generated!',
                        html: `Token <strong>${json.data.token_no}</strong> generated for shift ${shiftTime}`,
                        icon: 'success',
                        confirmButtonColor: '#3085d6'
                    });
                } else {
                    Swal.fire({
                        title: 'Success!',
                        text: json.message,
                        icon: 'success',
                        confirmButtonColor: '#3085d6'
                    });
                }
                setShowModal(false);
                setPatientName('');
                setPatientMobile('');
            } else {
                Swal.fire('Error', json.message, 'error');
            }
        } catch (error) {
            Swal.fire('Network Error', 'Please check your internet connection and try again.', 'error');
        } finally {







            setLoading(false);
        }
    };

    return (
        <div className="bg-light" style={{ minHeight: '100vh' }}>
            {/* Hero Section - Compact */}
            <section
                className="position-relative d-flex align-items-center py-4"
                style={{
                    minHeight: '180px',
                    backgroundImage: `url(${aboutHeroImg})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat'
                }}
            >
                <div
                    style={{
                        position: 'absolute',
                        inset: 0,
                        background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.8), rgba(15, 23, 42, 0.5))',
                        zIndex: 1
                    }}
                ></div>
                <div className="container position-relative text-center text-white" style={{ zIndex: 2 }}>
                    <h2 className="fw-bold mb-1">Self <span className="text-warning">Token</span></h2>
                    <p className="small mb-0 opacity-75">Self Token Booking For Patients</p>
                </div>
            </section>

            {/* Content Area with Side Images */}
            <div className="container py-3">
                <div className="row justify-content-center align-items-start">
                    {/* Left Side Image */}
                    <div className="col-lg-3 col-md-2 d-none d-md-block">
                        <div className="text-center">
                            <img 
                                src={giphyImg} 
                                alt="Left decoration" 
                                className="img-fluid rounded-3 shadow-sm"
                                style={{ maxHeight: '500px', width: '100%', objectFit: 'cover' }}
                            />
                        </div>
                    </div>
                    
                    {/* Main Content */}
                    <div className="col-lg-6 col-md-8 col-sm-12">

                        {/* Doctor Name Top Box - Dynamic & Compact */}
                        <div className="card shadow-sm border-0 rounded-4 mb-3 bg-white">
                            <div className="card-body py-2 px-3">
                                <div className="row align-items-center">
                                    <div className="col-md-7 text-start d-none d-md-block">
                                        <h6 className="mb-0 fw-bold text-dark" style={{ fontSize: '15px' }}>
                                            {selectedDoctor ? `${selectedDoctor.name_title || ''} ${selectedDoctor.fullname || ''}`.trim() : userData?.name}
                                        </h6>
                                        <p className="text-muted mb-0 fw-bold" style={{ fontSize: '12px' }}>
                                            Live Overview & Token Management
                                        </p>
                                    </div>
                                    <div className="col-md-5 text-md-end d-none d-md-block">
                                        <p className="text-muted mb-1 fw-bold" style={{ fontSize: '12px' }}>
                                            {moment().format('dddd, DD MMMM YYYY')}
                                        </p>
                                        <p className="text-primary mb-0 fw-bold" style={{ fontSize: '18px', fontFamily: 'monospace', letterSpacing: '2px' }}>
                                            {currentTime.format('HH:mm:ss')}
                                        </p>
                                    </div>

                                    {/* Mobile View for Box */}
                                    <div className="col-12 text-center d-md-none">
                                        <h6 className="mb-0 fw-bold text-dark" style={{ fontSize: '15px' }}>
                                            {selectedDoctor ? `${selectedDoctor.name_title || ''} ${selectedDoctor.fullname || ''}`.trim() : userData?.name}
                                        </h6>
                                        <p className="text-muted mb-1 fw-bold" style={{ fontSize: '12px' }}>
                                            Live Overview & Token Management
                                        </p>
                                        <div className="border-top pt-1 mt-1">
                                            <p className="text-muted mb-1 fw-bold" style={{ fontSize: '12px' }}>
                                                {moment().format('dddd, DD MMMM YYYY')}
                                            </p>
                                            <p className="text-primary mb-0 fw-bold text-center" style={{ fontSize: '18px', fontFamily: 'monospace', letterSpacing: '2px' }}>
                                                {currentTime.format('HH:mm:ss')}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Main Generate Card */}
                        <div className="card border-0 shadow-sm rounded-4 overflow-hidden mb-4 bg-white">
                            <div className="card-header bg-white border-0 pt-4 pb-2 text-center">
                                <div className="mb-2">
                                    <i className="bi bi-ticket-perforated text-warning" style={{ fontSize: '2.5rem' }}></i>
                                </div>
                                <h4 className="fw-bold">Generate Token</h4>
                                <p className="text-muted small">Tap the button for instant self-token</p>
                            </div>

                            <div className="card-body p-4 pt-2 text-center">
                                {getShiftInfo(selectedDoctor).index === null || getShiftInfo(selectedDoctor).isDisable ? (
                                    <div className="alert alert-danger py-2 mb-3 text-center small fw-bold" style={{ fontSize: '13px' }}>
                                        Doctor unavailable. No active shift.
                                    </div>
                                ) : null}
                                <button
                                    className="btn btn-primary w-100 py-3 rounded-pill fw-bold text-white shadow-sm"
                                    onClick={() => {
                                        setPatientName('');
                                        setPatientMobile('');
                                        setShowModal(true);
                                    }}
                                    disabled={loading || getShiftInfo(selectedDoctor).index === null || getShiftInfo(selectedDoctor).isDisable}
                                >
                                    <i className="bi bi-plus-circle me-2"></i>
                                    Create Self Token
                                </button>

                                <p className="mt-3 text-muted small mb-0">Note: This will be counted as In-Clinic token</p>
                            </div>
                        </div>

                        {/* Simple Back button */}
                        <div className="text-center mt-2">
                            <button
                                onClick={() => navigate(-1)}
                                className="btn btn-outline-secondary border-0 small px-4 py-2"
                                style={{ borderRadius: '25px', fontSize: '13px' }}
                            >
                                <i className="bi bi-arrow-left me-2"></i> Back to Home
                            </button>
                        </div>
                    </div>
                    
                    {/* Right Side Image */}
                    <div className="col-lg-3 col-md-2 d-none d-md-block">
                        <div className="text-center">
                            <img 
                                src={giphyImg} 
                                alt="Right decoration" 
                                className="img-fluid rounded-3 shadow-sm"
                                style={{ maxHeight: '500px', width: '100%', objectFit: 'cover' }}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Token Generation Modal */}
            {showModal && (
                <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1060 }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content rounded-4 border-0 shadow-lg">
                            <div className="modal-header border-0 pb-0">
                                <h5 className="modal-title fw-bold">
                                    <i className="fas fa-user-plus me-2 text-primary"></i>
                                    Generate Self Token
                                </h5>
                                <button type="button" className="btn-close" onClick={() => setShowModal(false)}></button>
                            </div>
                            <div className="modal-body p-4">
                                <div className="mb-3">
                                    <label className="form-label small fw-bold">Patient Name(Optional)</label>
                                    <input
                                        type="text"
                                        className="form-control rounded-3"
                                        placeholder="Enter patient name"
                                        value={patientName}
                                        onChange={e => setPatientName(e.target.value)}
                                    />
                                </div>
                                <div className="mb-4">
                                    <label className="form-label small fw-bold">Mobile Number *</label>
                                    <input
                                        type="tel"
                                        className="form-control rounded-3"
                                        placeholder="10 digit mobile number"
                                        maxLength={10}
                                        value={patientMobile}
                                        onChange={e => setPatientMobile(e.target.value.replace(/\D/g, ''))}
                                    />
                                    <small className="text-muted">Required for token generation</small>
                                </div>
                                <div className="d-flex gap-2 mt-4">
                                    <button
                                        className="btn btn-outline-secondary w-50 py-3 rounded-3 fw-bold"
                                        onClick={() => setShowModal(false)}
                                        disabled={loading}
                                    >
                                        CANCEL
                                    </button>
                                    <button
                                        className="btn btn-success w-50 py-3 rounded-3 fw-bold"
                                        onClick={() => executeSMSToken(patientName, patientMobile)}
                                        disabled={loading || !patientMobile || patientMobile.length !== 10}
                                    >
                                        {loading ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                                SENDING...
                                            </>
                                        ) : (
                                            <>
                                                <i className="bi bi-chat-left-dots me-1"></i> SEND SMS
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SelfMode;